import { Component, OnInit } from '@angular/core';
import { AuthService } from "../../services/auth.service";
import { UsersService } from "../../services/users.service";
import { Notification } from "../../model/notification";
import { Constants } from "../../utils/constants";
import { MessageData } from "../../model/messageData";
import { SplitCamelCasePipe } from "../../pipes/split-camel-case.pipe";
import { EMPTY, Observable, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { SnackService } from "../../services/snack.service";


@Component({
  selector: 'aw-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit {

  notifications: Notification[] = [];
  lastNotification?: Notification;
  notificationsToDismiss: Notification[] = [];

  loading = false;
  loadingMore = false;
  noMoreNotifications = false;

  badgeNumber = 0;
  skipBatch = 0;
  userGroup = '';

  allNotifications$!: Observable<any>;

  private readonly entities = Constants.entities;
  private readonly roles = Constants.roles;
  private loggedUser: any;

  constructor(private authService: AuthService,
    private usersService: UsersService,
    private snackService: SnackService,
    private splitCamelCase: SplitCamelCasePipe
  ) {
    this.loggedUser = this.authService.loggedUser();

    switch (this.loggedUser.role) {

      case this.roles.admin:
        this.userGroup = this.entities.admins;
        break;
      case this.roles.customer:
        this.userGroup = this.entities.customers;
        break;
      case this.roles.researcher:
        this.userGroup = this.entities.researchers;
        break;
      case this.roles.logistic:
        this.userGroup = this.entities.logistics;
        break;
      case this.roles.tester:
        this.userGroup = this.entities.testers;
        break;
      default:
        return;
    }

  }

  ngOnInit(): void {
    this.allNotifications$ =
      of(true)
        .pipe(
          tap(() => this.loading = this.skipBatch === 0),
          switchMap(() => 
          this.usersService.getNotifications(this.skipBatch, this.loggedUser.id, this.userGroup)),
          tap(notifications => {

            notifications.forEach(notification => {

              if (notifications.length < Constants.general.notificationsBatchSize)
                this.noMoreNotifications = true;

              if (!notification.isRead)
                this.badgeNumber++;

              this.notifications.push(notification);

              if (notifications.indexOf(notification) === (notifications.length - 1))
                this.lastNotification = notification;

            });


          }),
          tap(() => this.loading = false));

    this.allNotifications$.subscribe();
  }

  buildNotificationMessage(parsedJsonMessageBuildingData: MessageData, notificationType?: string): string {

    let builtMessage = '';

    switch (notificationType) {

      case Constants.notificationTypes.newCompanyAccountRequest:
        builtMessage = `The company "${parsedJsonMessageBuildingData.company?.companyName}" has just submitted a sign in request.`;
        break;

      case Constants.notificationTypes.newResearcherInvited:
        if (parsedJsonMessageBuildingData.researchers && parsedJsonMessageBuildingData.researchers.length === 1) {
          const researcher = parsedJsonMessageBuildingData.researchers[0];
          builtMessage = `The researcher "${researcher.firstName} ${researcher.lastName}" has just been invited.`;
        }
        break;

      case Constants.notificationTypes.accountActivationChanged:
        const enabledOrDisabled = parsedJsonMessageBuildingData.enabled ? 'enabled' : 'disabled';
        builtMessage = `The ${parsedJsonMessageBuildingData.userEnabledOrDisabled?.role} user "${parsedJsonMessageBuildingData.userEnabledOrDisabled?.firstName} ${parsedJsonMessageBuildingData.userEnabledOrDisabled?.lastName}" has just been ${enabledOrDisabled}.`;
        break;

      case Constants.notificationTypes.contractAboutToExpire:
        builtMessage = `The contract "${parsedJsonMessageBuildingData.contract?.title}" is about to expire.`;
        break;

      case Constants.notificationTypes.contractCreated:
        if (this.loggedUser.role === this.roles.admin)
          builtMessage = `The contract "${parsedJsonMessageBuildingData.contract?.title}" for the company "${parsedJsonMessageBuildingData.company?.companyName}" has just been created.`;
        else if (this.loggedUser.role === this.roles.customer && this.loggedUser.id === parsedJsonMessageBuildingData.contract?.contractReferent?.id)
          builtMessage = `You are the referent of the newly created contract "${parsedJsonMessageBuildingData.contract?.title}"`;
        break;

      case Constants.notificationTypes.contractStatusTransition:
        builtMessage = `The contract "${parsedJsonMessageBuildingData.contract?.title}" just transitioned from status "${this.splitCamelCase.transform(parsedJsonMessageBuildingData.previousStatus)}" to status "${this.splitCamelCase.transform(parsedJsonMessageBuildingData.appliedStatus)}".`;
        break;

      case Constants.notificationTypes.boxRequestToLogistic:
        if (parsedJsonMessageBuildingData.nOfBoxes && parsedJsonMessageBuildingData.nOfBoxes > 1)
          builtMessage = `There are ${parsedJsonMessageBuildingData.nOfBoxes} boxes needed for the contract "${parsedJsonMessageBuildingData.contract?.title}".`;
        else
          builtMessage = `There is ${parsedJsonMessageBuildingData.nOfBoxes} box needed for the contract "${parsedJsonMessageBuildingData.contract?.title}".`;
        break;

      case Constants.notificationTypes.boxCreated:
        builtMessage = `The box "${parsedJsonMessageBuildingData.box?.identifier}" has just been created.`;
        break;

      case Constants.notificationTypes.boxStatusTransition:
        builtMessage = `The box "${parsedJsonMessageBuildingData.box?.identifier}" just transitioned from status "${this.splitCamelCase.transform(parsedJsonMessageBuildingData.previousStatus)}" to status "${this.splitCamelCase.transform(parsedJsonMessageBuildingData.appliedStatus)}".`;
        break;

      case Constants.notificationTypes.newResearcherAssignedToBox:

        if (parsedJsonMessageBuildingData.researchers) {
          if (parsedJsonMessageBuildingData.researchers.length === 1) {
            const researcher = parsedJsonMessageBuildingData.researchers[0];
            builtMessage = `The researcher "${researcher.firstName} ${researcher.lastName}" has just been assigned to the box "${parsedJsonMessageBuildingData.box?.identifier}".`;
          } else {

            let researchers = "";

            for (let i = 0; i < parsedJsonMessageBuildingData.researchers.length; i++) {
              researchers += `${parsedJsonMessageBuildingData.researchers[i].firstName} ${parsedJsonMessageBuildingData.researchers[i].lastName}`;
              researchers += (i < parsedJsonMessageBuildingData.researchers.length - 1) ? ', ' : '';
            }

            builtMessage = `The researchers "${researchers}" have just been assigned to the box "${parsedJsonMessageBuildingData.box?.identifier}".`;
          }
        }
        break;

      case Constants.notificationTypes.researcherRemovedFromBox:

        if (parsedJsonMessageBuildingData.researchers) {
          if (parsedJsonMessageBuildingData.researchers.length === 1) {
            const researcher = parsedJsonMessageBuildingData.researchers[0];
            builtMessage = `The researcher "${researcher.firstName} ${researcher.lastName}" has just been removed from the box "${parsedJsonMessageBuildingData.box?.identifier}".`;
          } else {

            let researchers = "";

            for (let i = 0; i < parsedJsonMessageBuildingData.researchers.length; i++) {
              researchers += `${parsedJsonMessageBuildingData.researchers[i].firstName} ${parsedJsonMessageBuildingData.researchers[i].lastName}`;
              researchers += (i < parsedJsonMessageBuildingData.researchers.length - 1) ? ', ' : '';
            }

            builtMessage = `The researchers "${researchers}" have just been removed from the box "${parsedJsonMessageBuildingData.box?.identifier}".`;
          }
        }

        break;

      case Constants.notificationTypes.simIsAboutToExpire:
        builtMessage = `The sim "${parsedJsonMessageBuildingData.sim?.identifier}" is about to expire.`;
        break;

      case Constants.notificationTypes.certificateIsAboutToExpire:
        builtMessage = `The certificate "${parsedJsonMessageBuildingData.boxCertificate?.cmId}" is about to expire.`;
        break;

      case Constants.notificationTypes.testbedDeleted:
        builtMessage = `The testbed "${parsedJsonMessageBuildingData.testbed?.identifier}" has just been deleted.`;
        break;

      case Constants.notificationTypes.storageDeleted:
        builtMessage = `The storage "${parsedJsonMessageBuildingData.storage?.bucketId}" has just been deleted.`;
        break;

      case Constants.notificationTypes.newDocumentUploaded:
        builtMessage = `The document "${parsedJsonMessageBuildingData.filename}" has just been uploaded to the testbed "${parsedJsonMessageBuildingData.testbed?.identifier}".`;
        break;

      case Constants.notificationTypes.documentDeleted:
        builtMessage = `The document "${parsedJsonMessageBuildingData.filename}" has just been deleted from the testbed "${parsedJsonMessageBuildingData.testbed?.identifier}".`;
        break;

      case Constants.notificationTypes.storageResized:
        builtMessage = `The storage "${parsedJsonMessageBuildingData.storage?.bucketId}" has just been resized to "${parsedJsonMessageBuildingData.storage?.totalSpace}" MB..`;
        break;

      default:
        break;

    }

    return builtMessage;


  }

  markAsRead(notifications: Notification[]): void {

    notifications.forEach(notification => {

      if (!notification.isRead) {

        notification.isRead = true;
        this.notificationsToDismiss.push(notification);
        this.badgeNumber--;
      }
    });

  }

  saveNotificationsMarkedAsRead(): void {

    if (this.notificationsToDismiss.length !== 0) {

      const ids = this.notificationsToDismiss.filter(value => value !== undefined).map(value => value.id);

      this.usersService.markNotificationsAsRead(this.loggedUser.id, this.userGroup, { ids }).subscribe();

      // Done here and not in subscribe to avoid multiple equal http requests
      this.notificationsToDismiss = [];
    }

  }

  loadMoreNotifications(): void {
    this.loadingMore = true;
    this.skipBatch++;
    this.usersService.getNotifications(this.skipBatch, this.loggedUser.id, this.userGroup)
      .pipe(switchMap(value => value ? this.allNotifications$ : EMPTY))
      .subscribe(
        () => {
          this.loadingMore = false;
        },
        (error) => {
          this.loadingMore = false;
          this.snackService.error(error.message);
        }, () => {
          this.loadingMore = false;
        });
  }

  // openNotificationsDialog() {
  //   this.dialog.open(NotificationsDialogComponent, {data: this.notifications})
  // }
}
